import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { updateContactDetails } from "../../utils/requestHandler";
import { toast } from "react-toastify";
import {
  canadaProvincesAndTerritories,
  countries,
  usStates,
} from "../../utils/helperFunctions/countries-&-states";

const UpdateContactModal = ({
  setShowContactModal,
  guest_info,
  guest_id,
  fetchBookingValidation,
}) => {
  const bookingDetails = useSelector((state) => state.bookingValidation.data);
  const [contactDetails, setContactDetails] = useState({
    first_name: guest_info.first_name,
    last_name: guest_info.last_name,
    email: guest_info.email,
    guest_dob: guest_info.dob,
    phone: guest_info.phone,
    country: guest_info.country,
    address: guest_info.address,
    city: guest_info.city,
    state: guest_info.state,
    zipcode: guest_info.zipcode,
    guest_gender: guest_info.gender,
    guest_id: guest_id,
    booking_id: bookingDetails.booking_id,
    marketing_opt_in: false,
    declaration: false,
  });

  const validatePhone = (phone) => {
    // Ensure phone is exactly 10 digits
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email) => {
    // Regular expression for email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactDetails({
      ...contactDetails,
      [name]: value,
    });
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!validatePhone(contactDetails.phone)) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }

    if (!validateEmail(contactDetails.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    updateContactDetails(contactDetails)
      .then((result) => {
        toast.success("Contact details updated successfully.");
        setShowContactModal(false);
        fetchBookingValidation();
      })
      .catch((error) => {
        toast.error("An error occurred while updating travel documents.");
        console.log("error", error);
      });
  };

  return (
    <div>
      <div className="w-full max-w-lg mx-auto mt-10 p-6 relative bg-white shadow-md rounded-md">
        <div className="flex absolute right-2 top-2 justify-end">
          <div
            onClick={() => setShowContactModal(false)}
            className="cursor-pointer text-4xl"
          >
            &times;
          </div>
        </div>
        <div className="-m-6 p-6 mb-0 flex flex-col justify-center pb-0 bg-mv-lightblue">
          <div className="flex gap-5 text-mv-darkblue items-center">
            <h1 className="text-2xl uppercase font-bold mb-4">
              {guest_info.first_name + " " + guest_info.last_name}
            </h1>
            <p className="mb-2">D.O.B: {guest_info.dob}</p>
          </div>
          <p className="text-gray-600 mb-4">
            For name and date of birth edits, please reach out to our customer
            service team for assistance.{" "}
            <Link to="#" className="text-mv-darkblue underline">
              Contact customer service
            </Link>
          </p>
        </div>
        <h1 className="text-mv-darkblue uppercase font-semibold py-2 text-xl">
          Contact Information
        </h1>
        <form className="h-96 overflow-scroll pr-8">
          <div className="mb-4">
            <label className="block text-mv-darkblue font-semibold mb-1">
              Gender
            </label>
            <div className="flex items-center">
              <input
                type="radio"
                name="guest_gender"
                value="male"
                className="mr-2"
                disabled={guest_info.guest_gender}
                checked={contactDetails.guest_gender.toLowerCase() === "male"}
                onChange={handleChange}
              />
              <label className="mr-4">Male</label>
              <input
                type="radio"
                name="guest_gender"
                value="female"
                disabled={guest_info.guest_gender}
                className="mr-2 cursor-pointer"
                checked={contactDetails.guest_gender.toLowerCase() === "female"}
                onChange={handleChange}
              />
              <label className="mr-4">Female</label>
            </div>
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="email"
            >
              Email
            </label>
            <input
              disabled={guest_info.email}
              type="email"
              id="email"
              name="email"
              className="w-full px-3 py-2 border rounded-md"
              placeholder="name@email.com"
              value={contactDetails.email}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="phone"
            >
              Telephone
            </label>
            <input
              disabled={guest_info.phone}
              type="tel"
              id="phone"
              name="phone"
              className="w-full px-3 py-2 border rounded-md"
              placeholder="(555) 555-5555"
              value={contactDetails.phone}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="address"
            >
              Address
            </label>
            <input
              type="text"
              id="address"
              name="address"
              className="w-full px-3 py-2 border rounded-md"
              placeholder="1234 Somewhere Dr."
              value={contactDetails.address}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="country"
            >
              Country
            </label>
            {
              <select
                name="country"
                id="country"
                onChange={handleChange}
                value={contactDetails.country}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option disabled value="">
                  Select Country
                </option>
                {countries.map((country) => (
                  <option
                    key={country.countryCode}
                    value={country.country}
                    selected={contactDetails.country === country.country}
                  >
                    {country.country}
                  </option>
                ))}
              </select>
            }
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="state"
            >
              State/Province
            </label>
            {contactDetails.country === "United States" ||
            contactDetails.country === "Canada" ? (
              <select
                name="state"
                id="state"
                value={contactDetails.state}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option disabled value="">
                  Select State
                </option>
                {contactDetails.country === "United States" &&
                  usStates.map((state) => (
                    <option
                      key={state.code}
                      value={state.name}
                      selected={contactDetails.state === state.name}
                    >
                      {state.name}
                    </option>
                  ))}
                {contactDetails.country === "Canada" &&
                  canadaProvincesAndTerritories.map((province) => (
                    <option
                      key={province.code}
                      value={province.name}
                      selected={contactDetails.state === province.name}
                    >
                      {province.name}
                    </option>
                  ))}
              </select>
            ) : (
              <input
                type="text"
                id="state"
                name="state"
                className="w-full px-3 py-2 border rounded-md"
                value={contactDetails.state || ""}
                onChange={handleChange}
              />
            )}
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="city"
            >
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              className="w-full px-3 py-2 border rounded-md"
              value={contactDetails.city || ""}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label
              className="block text-mv-darkblue font-semibold mb-1"
              htmlFor="zipcode"
            >
              Zip Code
            </label>
            <input
              type="number"
              id="zipcode"
              name="zipcode"
              className="w-full px-3 py-2 border rounded-md"
              value={contactDetails.zipcode}
              onChange={handleChange}
            />
          </div>
          <div className="p-6">
            <p className="text-[#00407a] text-lg font-bold mb-3">
              Margaritaville at Sea highly recommends that all guests travel
              with a passport to book valid for at least six months after your
              return date for the following reasons:
            </p>
            <ul className="list-disc ml-5">
              <li className="mb-2 text-black text-lg font-normal leading-7">
                Guests traveling with Birth Certificates will experience
                significantly longer embarkation and debarkation.
              </li>
              <li className="mb-2 text-black text-lg font-normal leading-7">
                If you unexpectedly need to depart the ship from a foreign port
                prior to the end of sailing, a passport would be required to
                travel internationally by air.
              </li>
              <li className="mb-2 text-black text-lg font-normal leading-7">
                International ports of call can change their travel requirements
                at their sole discretion and may unexpectedly require all guests
                to have a valid passport to go ashore.
              </li>
            </ul>
            <p className="mt-5 mb-10">
              You never know when the unexpected might happen - like leaving the
              ship for a medical emergency - and it's always best to be
              prepared!
            </p>
            <div className="checkbox-container flex items-center gap-4 mb-6 mt-6">
              <input
                type="checkbox"
                onChange={(e) =>
                  setContactDetails({
                    ...contactDetails,
                    declaration: e.target.checked,
                  })
                }
                id="declaration"
                name="declaration"
                className="mr-2 transform hover:cursor-pointer scale-150"
              />
              <label for="declaration" className="text-sm text-gray-800">
                I declare that the above given details are true to the best of
                my knowledge and belief.{" "}
                <span className="text-red-600 text-2xl align-middle">*</span>
              </label>
            </div>
            <div className="checkbox-container flex items-center gap-4 mb-6 mt-6">
              <input
                type="checkbox"
                id="marketing"
                onChange={(e) =>
                  setContactDetails({
                    ...contactDetails,
                    marketing_opt_in: e.target.checked ? true : false,
                  })
                }
                name="marketing"
                className="mr-2 transform hover:cursor-pointer scale-150"
              />
              <label for="marketing" className="text-sm text-gray-800">
                Yes, I would like to receive marketing emails from
                Margaritaville at Sea, and I certify that I am a U.S. resident
                over the age of 13.
              </label>
            </div>
          </div>
          <div className="submit flex flex-col items-center">
            <Link
              to="#"
              className="save-link ml-4 text-[#4b6c8f]"
              id="save-guest-doc"
            ></Link>
            <button
              disabled={!contactDetails.declaration}
              onClick={handleSave}
              type="submit"
              className={`${
                !contactDetails.declaration
                  ? "bg-gray-400 text-black"
                  : "bg-[#00407a] text-white"
              } uppercase px-6 py-3`}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateContactModal;
